import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import uploadToS3 from "../api/uploadToS3.ts";
import AddCapsuleImage from "../api/AddCapsuleImage.ts";
import CameraCapture from "../components/Camera.tsx";
import { CircularProgress, Box, Typography, useMediaQuery, useTheme, Button } from "@mui/material";
import { ChevronLeft, Image as ImageIcon } from '@mui/icons-material';
import { fetchCapsule } from '../api/GetCapsules.ts';
import { useAuth } from '../context/AuthProvider.tsx';
import { motion } from 'framer-motion';

export default function CameraPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const mediaStreamRef = useRef<MediaStream | null>(null); // Ref to store the media stream
  const { user } = useAuth();
  const [isLocked, setIsLocked] = useState(false)


  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchCapsuleData = async () => {
      try {
        const fetchedCapsules = await fetchCapsule(user!.username, id!);
        if (fetchedCapsules.length > 0) {
          const capsule = fetchedCapsules[0];
          console.log(capsule)
          if (new Date(capsule.unlock) < new Date()) {
            console.log("here")
            setIsLocked(true)
          }
        }
      } catch (error) {
        console.error('Error fetching capsules:', error);
      }
    };

    fetchCapsuleData();
  }, [id]);

  const compressImage = useCallback((imageSrc: string, orientation: 'landscape' | 'portrait', quality: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = imageSrc;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Unable to get canvas context'));
          return;
        }

        let sourceWidth = img.width;
        let sourceHeight = img.height;
        let canvasWidth, canvasHeight, scaledWidth, scaledHeight, offsetX, offsetY;

        // Determine the target aspect ratio
        const targetAspectRatio = orientation === 'landscape' ? 16 / 9 : 3 / 4;

        // For mobile landscape, swap width and height
        if (isMobile && orientation === 'landscape') {
          [sourceWidth, sourceHeight] = [sourceHeight, sourceWidth];
        }

        // Set canvas dimensions
        canvasWidth = sourceWidth;
        canvasHeight = Math.round(canvasWidth / targetAspectRatio);

        // Calculate scaling
        if (sourceHeight / sourceWidth > targetAspectRatio) {
          // Image is taller than target ratio, scale based on width
          scaledWidth = canvasWidth;
          scaledHeight = (sourceHeight / sourceWidth) * scaledWidth;
          offsetX = 0;
          offsetY = (canvasHeight - scaledHeight) / 2;
        } else {
          // Image is wider than target ratio, scale based on height
          scaledHeight = canvasHeight;
          scaledWidth = (sourceWidth / sourceHeight) * scaledHeight;
          offsetX = (canvasWidth - scaledWidth) / 2;
          offsetY = 0;
        }

        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        // Clear the canvas
        ctx.fillStyle = 'black';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        // Draw the image
        if (isMobile && orientation === 'landscape') {
          ctx.save();
          ctx.translate(canvasWidth / 2, canvasHeight / 2);
          ctx.rotate(Math.PI / 2);
          ctx.drawImage(img, -scaledHeight / 2, -scaledWidth / 2, scaledHeight, scaledWidth);
          ctx.restore();
        } else {
          ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);
        }

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            } else {
              reject(new Error('Canvas to Blob conversion failed'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = (error) => reject(error);
    });
  }, [isMobile]);

  const handleCapture = useCallback(async (mediaSrc: string | Blob, newOrientation: 'landscape' | 'portrait', mediaType: 'image' | 'video') => {
    console.log(`Captured ${mediaType}:`, mediaSrc);

    try {
      setIsUploading(true);
      setUploadProgress(0);

      let uploadedMediaURL;

      if (mediaType === 'image') {
        const compressedImageSrc = await compressImage(mediaSrc as string, newOrientation, 0.8);
        setUploadProgress(33);

        uploadedMediaURL = await uploadToS3(compressedImageSrc);
      } else {
        // For video, we handle the Blob directly
        if (!(mediaSrc instanceof Blob)) {
          throw new Error('Invalid video data');
        }

        // Create a File from the Blob
        const file = new File([mediaSrc], `video-${Date.now()}.mp4`, { type: 'video/mp4' });

        uploadedMediaURL = await uploadToS3(file);
      }

      const filename = uploadedMediaURL!.split('/').pop();
      setUploadProgress(66);

      console.log(`Uploaded ${mediaType} URL:`, filename);

      await AddCapsuleImage(id!, filename!);

      setUploadProgress(100);

      setTimeout(() => {
        setIsUploading(false);
        navigate('/');
      }, 500);
    } catch (error) {
      console.error(`Error handling ${mediaType} capture:`, error);
      setIsUploading(false);
    }
  }, [compressImage, id, navigate]);

  const onClose = useCallback(() => {
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const cameraPermission = await navigator.permissions.query({ name: "camera" as PermissionName });
        const microphonePermission = await navigator.permissions.query({ name: "microphone" as PermissionName });

        if (cameraPermission.state === "granted" && microphonePermission.state === "granted") {
          setPermissionsGranted(true);
        } else if (cameraPermission.state === "prompt" || microphonePermission.state === "prompt") {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
          mediaStreamRef.current = stream; // Store the media stream
          setPermissionsGranted(true);
        } else {
          setPermissionsGranted(false);
        }

        const handlePermissionChange = () => {
          setPermissionsGranted(cameraPermission.state === "granted" && microphonePermission.state === "granted");
        };

        cameraPermission.addEventListener("change", handlePermissionChange);
        microphonePermission.addEventListener("change", handlePermissionChange);
        return () => {
          cameraPermission.removeEventListener("change", handlePermissionChange);
          microphonePermission.removeEventListener("change", handlePermissionChange);
        };
      } catch (error) {
        console.error("Permission API is not available in this browser:", error);
      }
    };

    checkPermissions();

    // Cleanup function to stop the media stream when the component unmounts
    return () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
      }
    };
  }, []);

  if (isUploading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: isMobile ? '100vh' : '100vh',
          bgcolor: '#121212',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background gradient effect */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at center, rgba(123, 44, 191, 0.1) 0%, rgba(18, 18, 18, 0) 70%)',
            zIndex: 0,
          }}
        />

        {/* Animated circles */}
        <Box
          sx={{
            position: 'absolute',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            border: '2px solid rgba(123, 44, 191, 0.2)',
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                opacity: 0.5,
              },
              '50%': {
                transform: 'scale(1.2)',
                opacity: 0.2,
              },
              '100%': {
                transform: 'scale(1)',
                opacity: 0.5,
              },
            },
          }}
        />

        {/* Content */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            textAlign: 'center',
            px: 3,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: 80,
              height: 80,
              mx: 'auto',
              mb: 3,
            }}
          >
            <CircularProgress
              variant="determinate"
              value={uploadProgress}
              size={80}
              thickness={4}
              sx={{
                color: 'white',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            />
          </Box>

          <Typography
            variant="h5"
            sx={{
              mb: 2,
              fontWeight: 600,
              background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Uploading your memory
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: 'white',
              mb: 4,
              maxWidth: 400,
              mx: 'auto',
            }}
          >
            {uploadProgress < 33
              ? "Preparing your media for upload..."
              : uploadProgress < 66
                ? "Uploading to secure storage..."
                : "Finalising your memory..."}
          </Typography>

          {/* Progress bar */}
          <Box
            sx={{
              width: '100%',
              maxWidth: 300,
              height: 4,
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              overflow: 'hidden',
              mx: 'auto',
            }}
          >
            <Box
              sx={{
                width: `${uploadProgress}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
                transition: 'width 0.3s ease-in-out',
                borderRadius: 2,
              }}
            />
          </Box>

          {/* Percentage indicator */}
          <Typography
            variant="h4"
            sx={{
              mt: 2,
              fontWeight: 700,
              color: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
            }}
          >
            {uploadProgress}%
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isLocked) {
    return (
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: '#0A0F15',
        color: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3
      }}>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          sx={{
            textAlign: 'center',
            maxWidth: 500,
            mb: 4
          }}
        >
          <Box sx={{ mb: 3 }}>
            <ImageIcon sx={{ fontSize: 80, mb: 2 }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
            Capsule Already Unlocked
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 18, mb: 4 }}>
            You are unable to add media to an already unlocked capsule.
          </Typography>
          <Button
            component={Link}
            to="/"
            variant="contained"
            startIcon={<ChevronLeft />}
            sx={{
              background: "linear-gradient(135deg, #7B2CBF, #E31B88)",
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 2,
            }}
          >
            Back to Home
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    permissionsGranted ? (
      <CameraCapture
        onCapture={handleCapture}
        onClose={onClose}
      />
    ) : (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: isMobile ? '100vh' : '100vh',
          textAlign: 'center',
          padding: '20px',
          color: '#94ADC7',
          fontFamily: 'Arial, sans-serif',
          backgroundColor: '#0A0F15'
        }}
      >
        <Typography variant="h4" sx={{ color: '#d9534f', mb: 2 }}>Camera and Microphone Permissions Needed</Typography>
        <Typography sx={{ maxWidth: '400px', mb: 3 }}>
          To use this feature, please enable camera and microphone access in your browser settings.
        </Typography>
        <Box
          component="button"
          onClick={() => window.location.reload()}
          sx={{
            padding: '10px 20px',
            backgroundColor: '#7B2CBF',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
          }}
        >
          Refresh Page
        </Box>
      </Box>
    )
  );
}
